import { EuOrganization, EuSite, SiteShare, Visitor } from '@noco/http-client/lib/noco'
import { GetServerSideProps } from 'next'
import { ReactElement, useMemo } from 'react'
import { GlobalLayoutEu } from 'src/components/commons'
import { MetaHeader } from 'src/components/organisms/MetaHeader'
import { PageSiteShareWithLink } from 'src/components/pages/PageSiteShareWithLink'
import { useGetContactSiteShare } from 'src/fixtures/modules/contactSite/hooks'
import { nocoSDK } from 'src/fixtures/modules/initialize'
import { getEuSiteMetaTitle } from 'src/fixtures/modules/site/utils'
import { SWRCachePath } from 'src/fixtures/modules/swr-cach-path'
import { getOrCreateVisitorFromToken } from 'src/fixtures/modules/visitor/functions'
import { deleteNoValue, getTokenFromCookie } from 'src/fixtures/utils'
import { ErrorRedirect, getEUViewErrorRedirect } from 'src/fixtures/utils/error'
import { NextPageWithLayout } from 'src/pages/_app'
import { SWRConfig } from 'swr'

type InitalProps = {}

type Props = {
  siteId: string
  shareId: string
  siteShare: SiteShare
  site: EuSite
  organization: EuOrganization
  visitor: Visitor
  fallback: { [key in string]: any }
} & InitalProps

const Index: NextPageWithLayout<Props> = ({ siteId, shareId, siteShare, fallback, site, organization }: Props) => {
  const { data } = useGetContactSiteShare(shareId)
  const title = useMemo(
    () =>
      getEuSiteMetaTitle({
        siteTitle: site?.title,
        organizationName: organization?.name,
        hideAd: organization?.hideAd
      }),
    [organization?.hideAd, organization?.name, site?.title]
  )

  return (
    <>
      <MetaHeader
        ogUrl={siteShare.url}
        ogType="website"
        ogTitle={site.siteMiscSetting?.ogpTitle}
        ogDescription={site.siteMiscSetting?.ogpDescription}
        ogImage={site.siteMiscSetting?.ogpFile?.url}
        title={title}
        noIndex={!site.siteMiscSetting?.isPublicSearchEngine}
      />
      <SWRConfig value={{ fallback }}>
        <PageSiteShareWithLink
          shareId={shareId}
          siteShare={siteShare}
          siteId={siteId}
          organization={organization}
          contactExpiredOn={data?.expiredOn}
          hasMaterialContactable={data?.hasMaterialContactable}
        />
      </SWRConfig>
    </>
  )
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <GlobalLayoutEu>{page}</GlobalLayoutEu>
}

export default Index

export const getServerSideProps: GetServerSideProps = async ctx => {
  try {
    nocoSDK.initalize()
    const { shareId } = ctx.query as { shareId?: string }
    const siteApi = nocoSDK.client?.contactService.siteApi
    if (!shareId || !siteApi) return ErrorRedirect.NotFound

    const { contactToken } = getTokenFromCookie(ctx)
    const res = await siteApi
      ?.ApiFactory(contactToken)
      .contactV1SitesSharePublicLinkUlidGet({ publicLinkUlid: shareId })
      .then(res => {
        return res.data
      })
      .catch(e => console.error(e))

    const site = res?.site
    const organization = res?.organization
    const canRequestToExtendExpiredAt = res?.canRequestToExtendExpiredAt
    if (!site || !organization || canRequestToExtendExpiredAt) return ErrorRedirect.NotFound

    // visitor 情報のリクエスト。 visitor
    let visit = await getOrCreateVisitorFromToken(ctx, organization.id!)
    if (!visit) return ErrorRedirect.NotFound
    // 共有リンクである場合には verify を叩く。-> document_contact が作られる
    if (site?.siteShare?.isActive && visit.hasContact) {
      await siteApi?.ApiFactory(contactToken).contactV1SitesSharePublicLinkUlidVerifyMaterialContactablePut({
        publicLinkUlid: site.siteShare.publicLinkUlid!
      })
    }

    // MEMO: @snuffy undefined 対策 (undeifined があると web 側にデータを流せないため)
    const siteShare = deleteNoValue(site.siteShare || {})
    const visitor = deleteNoValue(visit)

    return {
      props: {
        siteId: site.id,
        shareId: site.siteShare?.publicLinkUlid,
        site: deleteNoValue(site),
        siteShare,
        organization: deleteNoValue(organization),
        visitor,
        fallback: { [SWRCachePath.getVisitor()]: visitor }
      }
    }
  } catch (err: unknown) {
    return getEUViewErrorRedirect(err)
  }
}
